import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Buttons, CancelButton, Checkbox, Icon, ModalCard, Table } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import { colors } from '~/styles';

const S = {
  Table: styled(Table)`
    font-size: 0.875rem;
    max-height: 25rem;
    overflow-y: auto;
  `,

  Checkbox: styled.div`
    > label > div {
      background: ${colors.white};
      width: 1.125rem;
      height: 1.125rem;
      font-size: 0.5rem;
      margin: 0;
    }
  `,

  RoleName: styled.span`
    color: ${({ $inactive }) => ($inactive ? colors.grey40 : 'inherit')};
  `,

  TableState: styled.div`
    position: sticky;
    left: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    color: ${({ $error }) => ($error ? colors.danger : colors.grey40)};
    font-size: 0.875rem;
  `,
};

export default function ProjectMemberRolesModal({ project, projectMember, onSaved, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();

  const projectMemberId = projectMember.id;

  // The member's current assignments and the project's roles load together; the modal is
  // editable only once both have arrived, so a slow fetch can't be saved over.
  const [selectedIds, setSelectedIds] = useState(null);
  const [projectRoles, setProjectRoles] = useState(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const [member, allRoles] = await Promise.all([
          api.www.workspaces(workspace.id).projectSettings(project.id).members(projectMemberId).get(),
          api.www.workspaces(workspace.id).projectSettings(project.id).projectRoles().get(),
        ]);
        setSelectedIds(new Set((member.data.roles ?? []).map((pmr) => pmr.role.id)));
        setProjectRoles(allRoles.data);
      } catch {
        setHasLoadError(true);
      }
    })();
  }, [api, workspace.id, project.id, projectMemberId]);

  const isLoaded = !!selectedIds && !!projectRoles;

  const rows = useMemo(
    () => _.orderBy(projectRoles ?? [], [(role) => (role.isActive ? 0 : 1), 'name']),
    [projectRoles],
  );

  const handleToggle = (role) => {
    setSelectedIds((ids) => {
      const next = new Set(ids);
      if (next.has(role.id)) {
        next.delete(role.id);
      } else {
        next.add(role.id);
      }
      return next;
    });
  };

  const allSelected = rows.length > 0 && rows.every((role) => selectedIds?.has(role.id));

  const handleBulkToggle = () => {
    setSelectedIds(allSelected ? new Set() : new Set(rows.map((role) => role.id)));
  };

  const [{ isSubmitting }, form] = useForm();

  const handleSubmit = async () => {
    form.submit();

    try {
      await api.www
        .workspaces(workspace.id)
        .projectSettings(project.id)
        .members(projectMemberId)
        .upsert({ roles: rows.filter((role) => selectedIds.has(role.id)).map((role) => ({ projectRoleId: role.id })) });
      toast.success('Project Member saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
    }

    form.done();
  };

  return (
    <ModalCard title="Edit Role Assignments" byline={projectMember.member?.name} onClose={onClose}>
      <ModalCard.Body>
        <S.Table>
          <Table.Header sticky>
            <Table.Column align="center" width="3rem">
              <S.Checkbox>
                <Checkbox
                  aria-label="Select all roles"
                  disabled={rows.length === 0}
                  checked={!!selectedIds && selectedIds.size > 0}
                  partial={!!selectedIds && selectedIds.size > 0 && !allSelected}
                  onChange={handleBulkToggle}
                />
              </S.Checkbox>
            </Table.Column>
            <Table.Column>Project Role</Table.Column>
          </Table.Header>

          <Table.Body showNoResults={isLoaded}>
            {hasLoadError ? (
              <Table.Row>
                <S.TableState $error role="alert">
                  Unable to load project roles.
                </S.TableState>
              </Table.Row>
            ) : !isLoaded ? (
              <Table.Row>
                <S.TableState>
                  <Icon icon="spinner" spin />
                  Loading project roles...
                </S.TableState>
              </Table.Row>
            ) : (
              rows.map((role) => (
                <Table.Row key={role.id}>
                  <Table.Cell>
                    <S.Checkbox>
                      <Checkbox
                        aria-label={`Select ${role.name}`}
                        checked={selectedIds.has(role.id)}
                        onChange={() => handleToggle(role)}
                      />
                    </S.Checkbox>
                  </Table.Cell>
                  <Table.Cell>
                    <S.RoleName $inactive={!role.isActive}>{role.name}</S.RoleName>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </S.Table>
      </ModalCard.Body>

      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={onClose}>Close</CancelButton>

          <Button disabled={!isLoaded} isLoading={isSubmitting} onClick={handleSubmit}>
            Save &amp; Close
          </Button>
        </Buttons>
      </ModalCard.Footer>
    </ModalCard>
  );
}
